import { Component, signal, inject, computed, OnInit, OnDestroy, viewChild, ViewChild, ElementRef, HostBinding, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { MENU_ITEMS } from '../sidebar/menu.config';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { GoogleIdentityService } from '../../core/services/google-identity.service';
import { userDisplayName, userInitials } from '../../core/services/user-display';
import { IconComponent } from '../../shared/icons/icon.component';

import { ThemeToggleComponent } from '../../shared/components/theme-toggle/theme-toggle.component';
import { filter } from 'rxjs/operators';
import { Subscription, interval } from 'rxjs';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, IconComponent, ThemeToggleComponent],
  templateUrl: './navbar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ``
})
export class NavbarComponent implements OnInit, OnDestroy {

  @ViewChild('profileRef') profileRef!: ElementRef<HTMLDivElement>;

  private authService = inject(AuthService);
  private googleIdentity = inject(GoogleIdentityService);
  private router = inject(Router);
  private routerSubscription?: Subscription;
  private timeSubscription?: Subscription;
  private syncTimeoutId?: number;

  showUserMenu = signal(false);
  searchQuery = signal('');
  breadcrumbs = signal<string>('Dashboard');
  currentDateTime = signal<string>('');

  user = computed(() => this.authService.currentUser());
  displayName = computed(() => userDisplayName(this.user()));
  initials = computed(() => userInitials(this.user()));

  /** Set when the photo fails to load (expired URL, offline), so the icon shows instead. */
  photoFailed = signal(false);
  /** The Google photo, only for a user who signed in with Google. */
  photoUrl = computed(() =>
    this.photoFailed() ? null : this.googleIdentity.profilePhoto.photoFor(this.user()?.email),
  );


  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.showUserMenu() && this.profileRef) {
      const clickedInside = this.profileRef.nativeElement.contains(event.target as Node);
      if (!clickedInside) {
        this.closeUserMenu();
      }
    }
  }

  // Remove static routeMap, use dynamic label from URL

  ngOnInit(): void {
    this.updateBreadcrumbs(this.router.url);
    this.updateDateTime();
    this.setupTimeUpdates();

    // Listen to route changes
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updateBreadcrumbs(event.url);
      });
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.timeSubscription?.unsubscribe();
    if (this.syncTimeoutId) {
      clearTimeout(this.syncTimeoutId);
    }
  }

  private updateBreadcrumbs(url: string): void {
    // Remove query params and hash
    const cleanUrl = url.split('?')[0].split('#')[0];
    // Find parent and child menu items matching the route
    let parentLabel = 'Dashboard';
    let childLabel = '';
    for (const parent of MENU_ITEMS) {
      if (parent.route && cleanUrl.startsWith(parent.route)) {
        parentLabel = parent.label;
        if (parent.children) {
          for (const child of parent.children) {
            if (child.route === cleanUrl) {
              childLabel = child.label;
              break;
            }
          }
        }
        break;
      }
    }
    const label = childLabel ? `${parentLabel} / ${childLabel}` : parentLabel;
    this.breadcrumbs.set(label);
  }

  private updateDateTime(): void {
    const now = new Date();

    // PST/PDT time format
    const pstOptions: Intl.DateTimeFormatOptions = {
      timeZone: 'America/Los_Angeles',
      weekday: 'long',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };

    // IST time format (short form)
    const istOptions: Intl.DateTimeFormatOptions = {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };

    const pstFormatter = new Intl.DateTimeFormat('en-US', pstOptions);
    const istFormatter = new Intl.DateTimeFormat('en-US', istOptions);

    const pstFormatted = pstFormatter.format(now);
    const istFormatted = istFormatter.format(now);

    const combined = `${pstFormatted} (${istFormatted} IST)`;
    this.currentDateTime.set(combined);
  }

  private setupTimeUpdates(): void {
    // Calculate milliseconds until next minute for accurate sync
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    // Set initial timeout to sync with system clock
    this.syncTimeoutId = setTimeout(() => {
      this.updateDateTime();

      // Now set interval that runs exactly on minute boundaries
      this.timeSubscription = interval(60000).subscribe(() => {
        this.updateDateTime();
      });
    }, msUntilNextMinute) as unknown as number;
  }

  toggleUserMenu(): void {
    this.showUserMenu.update(show => !show);
  }

  handleLogout(): void {
    this.showUserMenu.set(false);
    this.authService.logout().subscribe({
      next: () => {
      },
      error: (error) => {
        console.error('Logout failed:', error);
      }
    });
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery.set(input.value);
  }

  closeUserMenu(): void {
    this.showUserMenu.set(false);
  }


   handleProfile(): void {
    this.showUserMenu.set(false);
    this.router.navigate(['app/home/profile']);
  }
}
